/**
 * Ableton Control: one button or a full row (DEC-291).
 *
 * Operator, 2026-10-18: *"each ableton control should have the option to have
 * its own 'full button grid'... rec quant needs moving into ableton control"*.
 * So the palette's Ableton Control tile asks WHAT to control, then ONE BUTTON
 * (a Button carrying one step, DEC-180) or FULL ROW (every value as its own
 * cell, lit from Live). The Rec Quantize tile is gone; its row is the Record
 * quantize full row.
 *
 * WHAT A ROW IS. A row of Ableton Control steps, one per cell, all of one
 * step type. A cell IS the step a one-step button would carry, so a tap
 * fires through the same dispatch, lights through the same `stepLit`, and a
 * button and a row convert into each other without translating anything.
 *
 * Record quantize is the exception on purpose: its row is the older
 * `recQuant` item kind, kept whole (OP-Quantize, the Amount cell, the
 * Live-at-None warning - DEC-273) rather than rebuilt here. Its cells are
 * labels, not steps, which is what DEC-181 already required of it.
 *
 * Pure functions only; the store wrappers live in `layout/store.ts`.
 */

import {
  LAUNCH_QUANT_LABELS, LAUNCH_QUANT_DISPLAY, normalizeLaunchQuant,
  REC_QUANT_DISPLAY, normalizeRecQuant,
  type AbletonStep, type AbletonClockTarget,
} from '@opbuttons/schema';
import { ulid } from '../utils/ulid.ts';
import { barsWords, curveLabel, CROSSFADE_ACTIONS, CROSSFADE_DEFAULT_BARS, crossfadeName, crossfadeWhere, motionBusLabel } from '../timeline/opMotion.ts';

/** The controls a full row exists for, besides Record quantize (its own kind). */
export type AbletonRowControl = 'tempo' | 'timeSig' | 'transport' | 'launchQuant' | 'practiceTransport' | 'crossfader';

/** Every Ableton Control the palette offers. */
export type AbletonControlId =
  | 'tempo' | 'timeSig' | 'transport' | 'recQuant' | 'launchQuant'
  | 'metronome' | 'endSessionRecord' | 'crossfader' | 'motionCapture' | 'motionLand' | 'practiceTransport' | 'sustainKill' | 'resync';

export interface AbletonControlChoice {
  id: AbletonControlId;
  /** The palette row's name, in the operator's words. */
  label: string;
  /** One plain line under it. */
  description: string;
  /** The step a one-button version carries. */
  stepType: AbletonStep['type'];
  /** What the full row is, or null when there is none to offer. */
  row: AbletonRowControl | 'recQuant' | null;
}

/** In the order the palette lists them: the clock first, then Live's
 *  session settings, then the two one-shot recovery controls. */
export const ABLETON_CONTROLS: readonly AbletonControlChoice[] = [
  { id: 'tempo', label: 'Tempo', description: 'Set the BPM. Row: your presets, 95 120 140 170 to start', stepType: 'ableton.tempo', row: 'tempo' },
  { id: 'timeSig', label: 'Time signature', description: 'Row: 3/4 4/4 6/8 7/8, pick your own', stepType: 'ableton.timesig', row: 'timeSig' },
  { id: 'transport', label: 'Transport', description: 'Play, Stop, Continue. Row: all three', stepType: 'ableton.transport', row: 'transport' },
  { id: 'recQuant', label: 'Record quantize', description: 'Row: None to 1/32, Live or OP-Quantize', stepType: 'ableton.recquant', row: 'recQuant' },
  { id: 'launchQuant', label: 'Launch quantize', description: 'Row: None, 1 Bar, 1/2, 1/4, 1/8 to start', stepType: 'ableton.launchquant', row: 'launchQuant' },
  { id: 'metronome', label: 'Metronome', description: "Live's click, lit while it is on", stepType: 'ableton.metronome', row: null },
  { id: 'endSessionRecord', label: 'End Session Record', description: "Turns Live's Session Record off, never on", stepType: 'ableton.endsessionrecord', row: null },
  { id: 'crossfader', label: 'Crossfader', description: 'Fade to A, the centre or B over bars, or flick to the other side (OP-Motion on Main). Row: A, C, B, Flick', stepType: 'opmotion.crossfade', row: 'crossfader' },
  { id: 'motionCapture', label: 'Capture (OP-Motion)', description: 'Saves what OP-Motion is set to now as a landing preset', stepType: 'opmotion.capture', row: null },
  { id: 'motionLand', label: 'Landing preset (OP-Motion)', description: 'Lands a captured preset: everything glides there by the next bar', stepType: 'opmotion.land', row: null },
  { id: 'practiceTransport', label: 'Practice transport', description: 'Play and Stop on the practice clock, no laptop needed', stepType: 'practice.transport', row: 'practiceTransport' },
  { id: 'sustainKill', label: 'Sustain kill', description: 'Let go of the sustain pedal on every track', stepType: 'ableton.sustainkill', row: null },
  { id: 'resync', label: 'Resync slave', description: "Snap the slave Ableton to the master's position", stepType: 'ableton.resync', row: null },
];

/** The choice a step belongs to. */
export function controlForStep(step: AbletonStep): AbletonControlChoice {
  return ABLETON_CONTROLS.find((c) => c.stepType === step.type)!;
}

/** The step type every cell of a row carries. */
export const ROW_STEP_TYPE: Record<AbletonRowControl, AbletonStep['type']> = {
  tempo: 'ableton.tempo',
  timeSig: 'ableton.timesig',
  transport: 'ableton.transport',
  launchQuant: 'ableton.launchquant',
  practiceTransport: 'practice.transport',
  crossfader: 'opmotion.crossfade',
};

/** The row control for a step type, or null when that step has no row of
 *  this kind (record quantize's row is `recQuant`; the one-shots have none). */
export function rowControlForStep(type: AbletonStep['type']): AbletonRowControl | null {
  const hit = (Object.keys(ROW_STEP_TYPE) as AbletonRowControl[]).find((k) => ROW_STEP_TYPE[k] === type);
  return hit ?? null;
}

/**
 * A fresh step of one type with that type's own sensible default. Was
 * `InspectorPanel.tsx::freshAbletonStep`; moved here when the palette needed
 * the same defaults, so the palette's "One button" and the Inspector's step
 * picker can never disagree about what a new step is.
 */
export function freshAbletonStep(type: AbletonStep['type'], target = ''): AbletonStep {
  const base = { id: ulid(), target };
  switch (type) {
    case 'ableton.tempo':       return { ...base, type, bpm: 120, clock: 'ableton' };
    case 'ableton.timesig':     return { ...base, type, numerator: 4, denominator: 4, clock: 'ableton' };
    case 'ableton.transport':   return { ...base, type, action: 'play' };
    case 'practice.transport':  return { ...base, type, action: 'play' };
    case 'ableton.recquant':    return { ...base, type, label: '16' };
    case 'ableton.launchquant': return { ...base, type, label: '1bar' };
    case 'ableton.metronome':   return { ...base, type, mode: 'toggle' };
    case 'ableton.resync':      return { ...base, type };
    case 'ableton.sustainkill': return { ...base, type };
    case 'ableton.endsessionrecord': return { ...base, type };
    case 'opmotion.crossfade':  return { ...base, type, action: 'flick' };
    case 'opmotion.capture':    return { ...base, type, bus: 'all' };
    case 'opmotion.land':       return { ...base, type, presetId: '' };
  }
}

// ─── Values ──────────────────────────────────────────────────────────────

/** Tempo presets a new row starts with (operator, 2026-10-18: "like 95, 120
 *  140 170, editable"). */
export const DEFAULT_TEMPO_PRESETS = [95, 120, 140, 170] as const;
export const TEMPO_MIN = 20;
export const TEMPO_MAX = 999;

/** Time signatures offered as one-tap picks in the Inspector; any other can
 *  be typed. The default row is the four a song set mostly uses. */
export const COMMON_TIME_SIGS: ReadonlyArray<readonly [number, number]> = [
  [2, 4], [3, 4], [4, 4], [5, 4], [6, 8], [7, 8], [9, 8], [12, 8],
];
export const DEFAULT_TIME_SIGS: ReadonlyArray<readonly [number, number]> = [[3, 4], [4, 4], [6, 8], [7, 8]];

/** Launch quantize values a new row starts with: None and the four a set
 *  is mostly played at. The other nine are a tap away in the Inspector. */
export const DEFAULT_LAUNCH_QUANTS = ['0', '1bar', '2', '4', '8'] as const;

export const TRANSPORT_ACTIONS = ['play', 'stop', 'continue'] as const;
export const PRACTICE_TRANSPORT_ACTIONS = ['play', 'stop'] as const;

/** Round to the 2 decimals the face can show, so 120.004 and 120 are one
 *  preset rather than two cells that read the same. */
export function roundBpm(n: number): number {
  return Math.round(Math.max(TEMPO_MIN, Math.min(TEMPO_MAX, n)) * 100) / 100;
}

/** A stable key for a cell, unique within its row: guard arming, share
 *  operation keys and smoke selectors all name a cell by it. */
export function cellKey(step: AbletonStep): string {
  switch (step.type) {
    case 'ableton.tempo':       return `bpm-${step.bpm}`;
    case 'ableton.timesig':     return `sig-${step.numerator}-${step.denominator}`;
    case 'ableton.transport':   return step.action;
    case 'practice.transport':  return step.action;
    case 'ableton.launchquant': return `lq-${step.label}`;
    case 'opmotion.crossfade':  return `xf-${step.action}`;
    default:                    return step.type;
  }
}

/** Format a BPM for a face: whole numbers bare, else up to 2 decimals. */
export function bpmText(bpm: number): string {
  return Number.isInteger(bpm) ? String(bpm) : String(roundBpm(bpm));
}

/** A cell's face, up to two lines. The second line carries the one fact the
 *  first cannot: which clock, or what a transport verb actually does here
 *  (the verbs are inverted between the two clocks, DEC-182). */
export function cellFace(step: AbletonStep): { l1: string; l2: string | null } {
  const practice = 'clock' in step && step.clock === 'practice';
  switch (step.type) {
    case 'ableton.tempo':      return { l1: bpmText(step.bpm), l2: practice ? 'practice' : 'BPM' };
    case 'ableton.timesig':    return { l1: `${step.numerator}/${step.denominator}`, l2: practice ? 'practice' : null };
    case 'ableton.transport':
      return step.action === 'play' ? { l1: 'Play', l2: 'from top' }
        : step.action === 'stop' ? { l1: 'Stop', l2: 'in place' }
        : { l1: 'Continue', l2: 'resume' };
    case 'practice.transport':
      return step.action === 'play' ? { l1: 'Play', l2: 'practice' } : { l1: 'Stop', l2: 'rewinds' };
    case 'ableton.launchquant': {
      const l = normalizeLaunchQuant(step.label);
      return { l1: l ? LAUNCH_QUANT_DISPLAY[l] : step.label, l2: null };
    }
    case 'opmotion.crossfade':
      return step.action === 'flick' ? { l1: 'Flick', l2: 'A / B' }
        : { l1: crossfadeName(step.action), l2: barsWords(step.bars ?? CROSSFADE_DEFAULT_BARS) };
    default: return { l1: step.type, l2: null };
  }
}

/**
 * The face of an UNNAMED one-step Ableton Control button: what it sets, read
 * at arm's length (DEC-291). Before, an unnamed button showed its value only
 * in edit mode, so the palette's "One button" landed a blank tile on stage -
 * and once it lit from Live, a lit blank tile. A name the operator types
 * still wins.
 */
export function buttonFace(s: AbletonStep, names: { preset?: (id: string) => string | undefined } = {}): string {
  const practice = 'clock' in s && s.clock === 'practice' ? ' practice' : '';
  switch (s.type) {
    case 'ableton.tempo':       return `${bpmText(s.bpm)} BPM${practice}`;
    case 'ableton.timesig':     return `${s.numerator}/${s.denominator}${practice}`;
    case 'ableton.transport':   return s.action === 'play' ? 'Play' : s.action === 'stop' ? 'Stop' : 'Continue';
    case 'practice.transport':  return s.action === 'play' ? 'Practice play' : 'Practice stop';
    case 'ableton.recquant': {
      const l = normalizeRecQuant(s.label);
      return `${s.via === 'op-quantize' ? 'OP-Q' : 'Rec'} ${l ? REC_QUANT_DISPLAY[l] : s.label}`;
    }
    case 'ableton.launchquant': {
      const l = normalizeLaunchQuant(s.label);
      return `Launch ${l ? LAUNCH_QUANT_DISPLAY[l] : s.label}`;
    }
    case 'ableton.metronome':   return s.mode === 'toggle' ? 'Metronome' : `Metronome ${s.mode}`;
    case 'ableton.sustainkill': return 'Sustain kill';
    case 'ableton.endsessionrecord': return 'End session rec';
    case 'opmotion.crossfade':  return s.action === 'flick' ? 'Crossfade flick' : `Fade to ${crossfadeWhere(s.action)}`;
    case 'opmotion.capture':    return s.bus === 'all' ? 'Capture' : `Capture ${motionBusLabel(s.bus)}`;
    case 'opmotion.land':       return (s.presetId && names.preset?.(s.presetId)) || 'No preset';
    case 'ableton.resync':      return 'Resync slave';
  }
}

/** A cell's tooltip: what a tap does, in full words. */
export function cellTitle(step: AbletonStep): string {
  const practice = 'clock' in step && step.clock === 'practice';
  switch (step.type) {
    case 'ableton.tempo':      return practice ? `Practice clock to ${bpmText(step.bpm)} BPM` : `Set Live's tempo to ${bpmText(step.bpm)} BPM`;
    case 'ableton.timesig':    return practice ? `Practice clock to ${step.numerator}/${step.denominator}` : `Set Live's time signature to ${step.numerator}/${step.denominator}`;
    case 'ableton.transport':
      return step.action === 'play' ? 'Play - Live restarts from the top and rolls. Lit while Live is playing.'
        : step.action === 'stop' ? 'Stop - Live pauses in place (a second stop goes back to the start). Lit while Live is stopped.'
        : 'Continue - Live resumes from where it stopped.';
    case 'practice.transport':
      return step.action === 'play' ? 'Play the practice clock. Lit while it runs.' : 'Stop the practice clock - it rewinds to the downbeat. Lit while it is stopped.';
    case 'ableton.launchquant': {
      const l = normalizeLaunchQuant(step.label);
      return l === '0' ? "Launch quantize None - clips launch the instant they are pressed" : `Set Live's launch quantize to ${l ? LAUNCH_QUANT_DISPLAY[l] : step.label}`;
    }
    case 'ableton.endsessionrecord': return "Turn Live's Session Record off. Never turns it on, so a double tap is safe.";
    case 'opmotion.crossfade':
      return step.action === 'flick' ? "Flick Live's crossfader to the other side, now (OP-Motion on Main)"
        : `Fade Live's crossfader to ${crossfadeWhere(step.action)} over ${barsWords(step.bars ?? CROSSFADE_DEFAULT_BARS)}, ${curveLabel(step.shape ?? 's').toLowerCase()}, from wherever it is (OP-Motion on Main)`;
    case 'opmotion.capture':    return `Save what OP-Motion on ${motionBusLabel(step.bus)} is set to now as a landing preset`;
    case 'opmotion.land':       return 'Land this preset: everything glides there and arrives on the next downbeat (OP-Motion)';
    default: return '';
  }
}

function step(type: AbletonStep['type'], fields: Record<string, unknown>): AbletonStep {
  return { id: ulid(), target: '', type, ...fields } as AbletonStep;
}

/** The clock a tempo or time-signature row drives (all its cells share one). */
export function rowClock(cells: readonly AbletonStep[]): AbletonClockTarget {
  const c = cells.find((s) => 'clock' in s);
  return c && 'clock' in c ? c.clock : 'ableton';
}

/** A new row's cells. */
export function defaultRowCells(control: AbletonRowControl, clock: AbletonClockTarget = 'ableton'): AbletonStep[] {
  switch (control) {
    case 'tempo':             return DEFAULT_TEMPO_PRESETS.map((bpm) => step('ableton.tempo', { bpm, clock }));
    case 'timeSig':           return DEFAULT_TIME_SIGS.map(([numerator, denominator]) => step('ableton.timesig', { numerator, denominator, clock }));
    case 'transport':         return TRANSPORT_ACTIONS.map((action) => step('ableton.transport', { action }));
    case 'launchQuant':       return DEFAULT_LAUNCH_QUANTS.map((label) => step('ableton.launchquant', { label }));
    case 'practiceTransport': return PRACTICE_TRANSPORT_ACTIONS.map((action) => step('practice.transport', { action }));
    case 'crossfader':        return CROSSFADE_ACTIONS.map((action) => step('opmotion.crossfade', action === 'flick' ? { action } : { action, bars: CROSSFADE_DEFAULT_BARS, shape: 's' }));
  }
}

/** Order key so a row always reads the same way round, whatever order its
 *  values were picked in: a row that reshuffles under the operator is one
 *  they cannot build muscle memory on (the Rec Quantize rule). */
function orderOf(s: AbletonStep): number {
  switch (s.type) {
    case 'ableton.tempo':       return s.bpm;
    case 'ableton.timesig':     return s.denominator * 1000 + s.numerator;
    case 'ableton.transport':   return TRANSPORT_ACTIONS.indexOf(s.action);
    case 'practice.transport':  return PRACTICE_TRANSPORT_ACTIONS.indexOf(s.action);
    case 'opmotion.crossfade':  return CROSSFADE_ACTIONS.indexOf(s.action);
    case 'ableton.launchquant': {
      const l = normalizeLaunchQuant(s.label);
      return l === null ? 99 : LAUNCH_QUANT_LABELS.indexOf(l);
    }
    default: return 0;
  }
}

/**
 * A row's cells made sound: only the row's own step type, values clamped and
 * canonical, one cell per value, in canonical order, every cell on the row's
 * clock. Returns [] for a set with nothing valid, which callers REFUSE (as
 * `setRecQuantLabels` does): a row with no cells reads as a broken item, and
 * clearing the last value is far likelier a slip than an intent.
 */
export function normaliseRowCells(control: AbletonRowControl, cells: readonly AbletonStep[]): AbletonStep[] {
  const want = ROW_STEP_TYPE[control];
  const clock = rowClock(cells);
  const seen = new Set<string>();
  const out: AbletonStep[] = [];
  for (const raw of cells) {
    if (raw.type !== want) continue;
    let s: AbletonStep = raw;
    if (s.type === 'ableton.tempo') {
      if (!Number.isFinite(s.bpm)) continue;
      s = { ...s, bpm: roundBpm(s.bpm), clock };
    } else if (s.type === 'ableton.timesig') {
      const n = Math.round(s.numerator);
      const d = Math.round(s.denominator);
      if (!(n >= 1 && n <= 99 && d >= 1 && d <= 32)) continue;
      s = { ...s, numerator: n, denominator: d, clock };
    } else if (s.type === 'ableton.launchquant') {
      const l = normalizeLaunchQuant(s.label);
      if (l === null) continue;
      s = { ...s, label: l };
    } else if (s.type === 'opmotion.crossfade' && s.action !== 'flick') {
      // A fade always has a length: a missing or nonsense one is the default, never a fade of nothing.
      if (!(s.bars !== undefined && Number.isFinite(s.bars) && s.bars > 0)) s = { ...s, bars: CROSSFADE_DEFAULT_BARS };
    }
    const k = cellKey(s);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(s);
  }
  return out.sort((a, b) => orderOf(a) - orderOf(b));
}

/** The row a one-step button becomes: the defaults with the button's own
 *  value in them, so nothing the operator chose is lost. */
export function rowCellsFromStep(control: AbletonRowControl, s: AbletonStep): AbletonStep[] {
  const clock = 'clock' in s ? s.clock : 'ableton';
  return normaliseRowCells(control, [...defaultRowCells(control, clock), { ...s, id: ulid(), target: '' }]);
}
